<template>
	<section
		ref="navSidebarGroup"
		:class="classes"
		@click="onClick"
		:data-selected="isSelected"
	>
		<NavSidebarTooltip
			class-name="nav-sidebar-group__tooltip"
			v-if="$slots.tooltip && $refs.navSidebarGroup"
			v-bind="tooltipBindings"
		>
			<slot name="tooltip"></slot>
		</NavSidebarTooltip>
		<div :class="headingClasses">
			<div class="header--left">
				<span class="nav-sidebar-group__icon" v-if="$slots.icon">
					<slot name="icon"></slot>
				</span>
				<h1 :class="textClasses">
					<slot name="text" v-if="$slots.text"></slot>
				</h1>
			</div>
			<div class="header--right" >
				<AddButton
					v-if="emits?.create"
					size="TINY"
					type="TERTIARY"
					@click.stop="$emit( emits.create.eventName, emits.create.eventArgs )"
				/>
				<div class="nav-sidebar__badge" v-if="$slots.badge">
					<slot name="badge"></slot>
				</div>
			</div>
		</div>
		<main class="nav-sidebar-group__items">
			<slot name="items"></slot>
		</main>
	</section>
</template>

<script>
import throttle from "lodash/throttle";
import { Enum } from "@teaming/common-enums";
import { AddButton } from "@teaming/common-ui";
import NavSidebarTooltip from "./NavSidebarTooltip";

export const Themes = Object.create( Enum );
Themes.PRIMARY = "PRIMARY";
Themes.SPECIAL = "SPECIAL";
Themes.PAYMENT = "PAYMENT";

export const Scales = Object.create( Enum );
Scales.NORMAL = "NORMAL";
Scales.SMALL = "SMALL";

export default {
	name: "NavSidebarGroup",
	emits: [ "click", ],
	components: {
		AddButton,
		NavSidebarTooltip,
	},
	props: {
		class: {
			type: String,
			default: "",
		},
		headingClass: {
			type: String,
			default: "",
		},
		isSelected: {
			type: Boolean,
			default: false,
		},
		isSubSelected: {
			type: Boolean,
			default: false,
		},
		theme: {
			type: String,
			default: "primary",
		},
		scale: {
			type: String,
			default: "normal",
		},
		emits: {
			type: Object,
			default: () => {},
		},
	},
	data() {
		return {
			resizeHandler: null,
			windowWidth: 0,
		};
	},
	computed: {
		classes() {
			return {
				"nav-sidebar-group": true,
				"nav-sidebar-group--selected": this.isSelected,
				"nav-sidebar-group--sub-selected": this.isSubSelected,
				[ this.class ]: !!this.class,
			};
		},
		headingClasses() {
			const baseClass = `nav-sidebar-group__heading ${ this.headingClass }`.trim();
			if ( this.theme === Themes.SPECIAL ) {
				return `${ baseClass } teaming-theme--special`;
			}
			if ( this.theme === Themes.PAYMENT ) {
				return `${ baseClass } teaming-theme--payment`;
			}
			return `${ baseClass } teaming-theme--primary`;
		},
		textClasses() {
			const baseClass = "nav-sidebar-group__text";
			if ( this.scale === Scales.SMALL ) {
				return `${ baseClass } teaming-scale--small`;
			}
			return `${ baseClass } teaming-scale--normal`;
		},
		tooltipBindings() {
			const bindings = {
				// force these bindings to recompute
				// whenever the window width changes
				_: this.windowWidth,
				groupTop: 0,
				groupBottom: 0,
				groupLeft: 0,
				groupRight: 0,
			};

			if ( !this.$refs.navSidebarGroup ) {
				return bindings;
			}

			const {
				top: groupTop,
				bottom: groupBottom,
				right: groupRight,
			} = this.$refs.navSidebarGroup.getBoundingClientRect();

			return {
				groupTop,
				groupBottom,
				groupRight,
			};
		},
	},
	methods: {
		onClick( e ) {
			e.stopPropagation();
			this.$emit( "click" );
		},
	},
	mounted() {
		this.resizeHandler = throttle( () => {
			this.windowWidth = window.innerWidth;
			// eslint-disable-next-line no-magic-numbers
		}, 200 );
		window.addEventListener( "resize", this.resizeHandler );
	},
	beforeUnmount() {
		if ( this.resizeHandler ) {
			window.removeEventListener( "resize", this.resizeHandler );
			this.resizeHandler = null;
		}
	},
};
</script>


<style lang="scss" scoped>
// base
$navSidebarGroupPrimaryHeadingColor: $color1-500;
$navSidebarGroupPrimaryHeadingBackgroundColor: $mono-000;
$navSidebarGroupSpecialHeadingColor: $color9-600;
$navSidebarGroupSpecialHeadingBackgroundColor: $color9-100;
$navSidebarGroupPaymentHeadingColor: $color4-600;
$navSidebarGroupPaymentHeadingBackgroundColor: $color4-100;
// selected
$navSidebarGroupPrimarySelectedHeadingColor: $color2-600;
$navSidebarGroupPrimarySubSelectedHeadingColor: $color2-500;
$navSidebarGroupPrimarySelectedHeadingBackgroundColor: $color2-100;
$navSidebarGroupSpecialSelectedHeadingColor: $mono-000;
$navSidebarGroupSpecialSelectedHeadingBackgroundColor: $color9-500;
// hover
$navSidebarGroupPrimaryHoverHeadingColor: $color1-600;
$navSidebarGroupPrimaryHoverHeadingBackgroundColor: $color1-100;
$navSidebarGroupSpecialHoverHeadingColor: $color9-100;
$navSidebarGroupSpecialHoverHeadingBackgroundColor: $color9-600;
$navSidebarGroupPaymentHoverHeadingColor: $white;
$navSidebarGroupPaymentHoverHeadingBackgroundColor: $color4-500;

.nav-sidebar-group:hover {
	.nav-sidebar-group__tooltip {
		@include for-size('md') {
			visibility: visible;
		}

		@include for-size('lg') {
			visibility: hidden;
		}
	}
}

.nav-sidebar-group__heading {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;

	border-radius: 0.5rem;
	padding: 0.5rem;

	cursor: pointer;
	position: relative;
}

.header--left, .header--right {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 0.5rem;
}

.header--right {
	display: flex;

	@include for-size('md') {
		display: none;
	}

	@include for-size('lg') {
		display: flex;
	}
}

.nav-sidebar-group--selected {
	.nav-sidebar-group__heading.teaming-theme--primary {
		color: $navSidebarGroupPrimarySelectedHeadingColor;
		background-color: $navSidebarGroupPrimarySelectedHeadingBackgroundColor;
	}

	.nav-sidebar-group__heading.teaming-theme--special {
		color: $navSidebarGroupSpecialSelectedHeadingColor;
		background-color: $navSidebarGroupSpecialSelectedHeadingBackgroundColor;
	}
}

.nav-sidebar-group--sub-selected {
	.nav-sidebar-group__heading.teaming-theme--primary {
		color: $navSidebarGroupPrimarySubSelectedHeadingColor;
	}
}

.nav-sidebar-group:not(.nav-sidebar-group--selected) {
	.nav-sidebar-group__heading.teaming-theme--primary:hover {
		color: $navSidebarGroupPrimaryHoverHeadingColor;
		background-color: $navSidebarGroupPrimaryHoverHeadingBackgroundColor
	}

	.nav-sidebar-group__heading.teaming-theme--special:hover {
		color: $navSidebarGroupSpecialHoverHeadingColor;
		background-color: $navSidebarGroupSpecialHoverHeadingBackgroundColor;
	}

	.nav-sidebar-group__heading.teaming-theme--payment:hover {
		color: $navSidebarGroupPaymentHoverHeadingColor;
		background-color: $navSidebarGroupPaymentHoverHeadingBackgroundColor;
	}
}

.nav-sidebar-group__heading.teaming-theme--primary {
	color: $navSidebarGroupPrimaryHeadingColor;
	background-color: $navSidebarGroupPrimaryHeadingBackgroundColor;
}

.nav-sidebar-group__heading.teaming-theme--special {
	color: $navSidebarGroupSpecialHeadingColor;
	background-color: $navSidebarGroupSpecialHeadingBackgroundColor;
}

.nav-sidebar-group__heading.teaming-theme--payment {
	color: $navSidebarGroupPaymentHeadingColor;
	background-color: $navSidebarGroupPaymentHeadingBackgroundColor;
}

.nav-sidebar-group__text {
	font-style: normal;
	font-weight: 600;
	// TODO: address off-scale font size with Jeff
	font-size: 1.05rem;
	line-height: 1.5rem;

	@include for-size('md') {
		display: none;
	}

	@include for-size('lg') {
		display: inline-flex;
	}
}

.nav-sidebar-group__text.teaming-scale--normal {
	font-style: normal;
	font-weight: 600;
	// TODO: address off-scale font size with Jeff
	font-size: 1.05rem;
	line-height: 1.5rem;
}

.nav-sidebar-group__text.teaming-scale--small {
	font-style: normal;
	font-weight: 600;
	// TODO: address off-scale font size with Jeff
	font-size: 0.875rem;
	line-height: 1.25rem;
}

.nav-sidebar-group__items {
	display: none;

	@include for-size('lg') {
		display: block;
	}
}

.nav-sidebar-group__tooltip {
	visibility: hidden;
}
.nav-sidebar__badge {
	display: inline;
	border-radius: 128px;
	padding: 1px 8px;
	background-color: $color7;
	color: $color1-050;
	font-size: 0.9rem;
	font-weight: 600;

}
</style>

<style lang="scss">
.nav-sidebar-group {
	.nav-sidebar-item:first-of-type {
		margin-top: 0.2rem;
	}

	.nav-sidebar-item:last-of-type {
		margin-bottom: 0;
	}
}
</style>
